import { TypeAccepted } from '@/components/Upload';
import { ApplicationState } from '@/store';
import { lookup } from 'mime-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export interface AcceptConfiguration {
    folder: string;
    accept: string;
    acceptLabel: string;
}

export const useTypesAcceptedUpload = (
    typeAccepted: TypeAccepted,
    isLogo: boolean = false,
    isBanner: boolean = false,
    shortLabel: boolean = false,
    customFolder: string | undefined,
    isFullImageTypeAccepted: boolean = false,
) => {
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);

    const [acceptConfiguration, setAcceptConfiguration] = useState<AcceptConfiguration>({ folder: '', accept: '', acceptLabel: '' });

    const convertToMimeType = (types: string[]) => {
        const mimeTypes = types.reduce((mimeTypes: string[], type: string) => {
            const mimeType = lookup(type);

            if (mimeType) {
                mimeTypes.push(mimeType);
            }
            return mimeTypes;
        }, []);

        return mimeTypes.join(',');
    };

    const getFolderDestination = (path: string) => (customFolder ? customFolder : path);

    useEffect(() => {
        switch (typeAccepted) {
            case 'IMAGE':
                if (isLogo) {
                    setAcceptConfiguration({
                        folder: getFolderDestination('logo/'),
                        accept: convertToMimeType(['png']),
                        acceptLabel: strings.ACCEPTED_PNG_FORMAT,
                    });
                } else if (isBanner) {
                    setAcceptConfiguration({
                        folder: getFolderDestination('banner/'),
                        accept: convertToMimeType(['png']),
                        acceptLabel: strings.ACCEPTED_PNG_FORMAT,
                    });
                } else if (isFullImageTypeAccepted) {
                    setAcceptConfiguration({
                        folder: getFolderDestination('media/'),
                        accept: convertToMimeType(['png', 'jpeg', 'bmp', 'gif']),
                        acceptLabel: strings.ACCEPTED_FULL_IMAGE_FORMATS,
                    });
                } else {
                    setAcceptConfiguration({
                        folder: getFolderDestination('media/'),
                        accept: convertToMimeType(['png', 'jpeg']),
                        acceptLabel: shortLabel ? strings.ACCEPTED_IMAGE_EXTENSIONS : strings.ACCEPTED_IMAGE_FORMATS,
                    });
                }
                break;
            case 'VIDEO':
                setAcceptConfiguration({
                    folder: getFolderDestination('media/'),
                    accept: convertToMimeType(['mp4', 'mpeg']),
                    acceptLabel: strings.ACCEPTED_VIDEO_FORMATS,
                });
                break;
            case 'SUBTITLE':
                setAcceptConfiguration({
                    folder: getFolderDestination('media/'),
                    accept: convertToMimeType(['vtt']),
                    acceptLabel: strings.ACCEPTED_SUBTITLE_FORMATS,
                });
                break;
            case 'AUDIO':
                setAcceptConfiguration({
                    folder: getFolderDestination('media/'),
                    accept: convertToMimeType(['mp3', 'ogg']),
                    acceptLabel: strings.ACCEPTED_AUDIO_FORMATS,
                });
                break;
            case 'ZIP':
                setAcceptConfiguration({
                    folder: getFolderDestination('courses/import/'),
                    accept: convertToMimeType(['zip']),
                    acceptLabel: strings.ACCEPTED_ZIP_FORMATS,
                });
                break;
            case 'ATTACHMENTS':
                setAcceptConfiguration({
                    folder: getFolderDestination('media/'),
                    accept: convertToMimeType(['pdf', 'ppt', 'pptx', 'xls', 'xlsx', 'jpg', 'png', 'gif', 'mp3', 'mp4', 'mpeg']),
                    acceptLabel: strings.ACCEPTED_ATTACHMENTS_FORMATS,
                });
                break;
            case 'SPREADSHEET':
                setAcceptConfiguration({
                    folder: getFolderDestination('media/'),
                    accept: convertToMimeType(['xlsx', 'xls']),
                    acceptLabel: '',
                });
                break;
            case 'DOCUMENT':
                setAcceptConfiguration({
                    folder: getFolderDestination('media/'),
                    accept: convertToMimeType(['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx']),
                    acceptLabel: strings.ACCEPTED_DOCUMENT_FORMATS,
                });
                break;
            default:
                setAcceptConfiguration({
                    folder: getFolderDestination('media/'),
                    accept: convertToMimeType(['png', 'jpeg']),
                    acceptLabel: shortLabel ? strings.ACCEPTED_IMAGE_EXTENSIONS : strings.ACCEPTED_IMAGE_FORMATS,
                });
        }
    }, [strings._LANGUAGE, typeAccepted]);

    return { acceptConfiguration };
};
